import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  COMMISSION_CATEGORIES,
  MATRIX_COMMISSION_TYPES,
  MATRIX_FILTERS,
  MATRIX_MAX_MONTHS,
  percentLabel,
} from '@app/shared';
import type {
  CommissionCategory,
  CommissionLineDto,
  CommissionMatrixDetailDto,
  CommissionMatrixDto,
  CommissionMatrixListDto,
  CommissionRatesDto,
  MatrixCommissionType,
  MatrixFilter,
  MatrixRate,
} from '@app/shared';
import { US_STATES } from '@app/shared';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { ErrorState, LoadingState } from '../../components/PageState';
import { StatusChip } from '../../components/StatusChip';
import { FormDialog } from '../../components/dialogs';
import { ApiError, api } from '../../lib/api';
import { formatCents, formatDate, formatDateTime } from '../../lib/format';
import { useFeatureReleased } from '../../release';
import { useAuth } from '../auth/AuthProvider';
import { CommissionRuleButtons } from './CommissionRules';

/**
 * Commission matrix (PC792) as the reference has it (product inventory, appendix 13.14):
 * "Manage Commission Matrix" with its cards and "Matrix List", and "Update commission matrix"
 * with one line per category, plan and state. "Filters", "AI Insights", "Export", "Add
 * Category", the redeem rule and the limit rule are not built.
 */

const BASE = '/commission-matrices';
const LIST_PATH = '/settings/users/commission';
type Problems = Record<string, string>;
const fromServer = (err: unknown): Problems =>
  err instanceof ApiError ? Object.fromEntries(err.details.map((d) => [d.path, d.message])) : {};
const dash = (v: string | number | null) => (v === null ? '-' : String(v));
const stateName = (code: string) => US_STATES.find(([c]) => c === code)?.[1] ?? code;

/* ---------------------------------- The dialog ------------------------------- */

/** The text of a $ and a % field, keyed `${month}:${category}`. */
type Amounts = Record<string, { flat: string; percent: string }>;
const keyOf = (month: number, category: string) => `${month}:${category}`;
const toAmounts = (rates: MatrixRate[]): Amounts =>
  Object.fromEntries(
    rates.map((r) => [
      keyOf(r.month, r.category),
      {
        flat: r.flatCents ? (r.flatCents / 100).toFixed(2) : '',
        percent: r.percentBp ? String(r.percentBp / 100) : '',
      },
    ]),
  );
const AMOUNT = /^\d{0,3}(\.\d{0,2})?$/;

type DialogMode = 'add' | 'update' | 'addState';
const TITLES: Record<DialogMode, string> = {
  add: 'Add matrix commission (PC1243)',
  update: 'Update matrix commission (PC1243)',
  addState: 'Add state to matrix (PC1243)',
};

interface DialogProps {
  mode: DialogMode;
  matrix?: CommissionMatrixDto;
  /** The matrix's states (update) and recurring amounts (update, add state). */
  detail?: CommissionMatrixDetailDto;
  /** A line's state and plan, when it was opened from the line's Edit. */
  preset?: { state: string; planId: string };
  onClose: () => void;
}

function MatrixDialog({ mode, matrix, detail, preset, onClose }: DialogProps) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [name, setName] = useState(matrix?.name ?? '');
  const [type, setType] = useState<MatrixCommissionType | ''>(matrix?.commissionType ?? '');
  const [state, setState] = useState(preset?.state ?? '');
  const [planId, setPlanId] = useState(preset?.planId ?? '');
  const [months, setMonths] = useState(matrix?.months ? String(matrix.months) : '');
  const [recurringPlan, setRecurringPlan] = useState(matrix?.recurringPlan ?? 'No');
  const [recurringDevice, setRecurringDevice] = useState(matrix?.recurringDevice ?? 'No');
  const start = toAmounts(detail?.recurring ?? []);
  const [amounts, setAmounts] = useState<Amounts>(start);
  const [problems, setProblems] = useState<Problems>({});
  const [loadedFor, setLoadedFor] = useState('');

  const plans = useQuery({
    queryKey: ['commission', 'plans', state],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>(`${BASE}/plans`, { state }),
    select: (r) => r.data,
    enabled: Boolean(state),
  });
  // Updating a state and plan starts from what the matrix holds for them.
  const rates = useQuery({
    queryKey: ['commission', matrix?.id, 'rates', state, planId],
    queryFn: () => api.get<CommissionRatesDto>(`${BASE}/${matrix!.id}/rates`, { state, planId }),
    enabled: mode === 'update' && Boolean(matrix && state && planId && planId !== 'all'),
  });
  const ratesKey = `${state}:${planId}`;
  if (rates.data && loadedFor !== ratesKey && rates.data.state === state) {
    setLoadedFor(ratesKey);
    setAmounts(toAmounts(rates.data.rates));
  }

  const save = useMutation({
    mutationFn: (body: unknown) =>
      mode === 'add'
        ? api.post<CommissionMatrixDto>(BASE, body)
        : mode === 'update'
          ? api.put<CommissionMatrixDto>(`${BASE}/${matrix!.id}/lines`, body)
          : api.post<CommissionMatrixDto>(`${BASE}/${matrix!.id}/states`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['commission'] }),
  });

  const recurring = type === 'Recurring';
  const monthCount = recurring ? Number(months) || 0 : 0;
  const amountField = (month: number, category: CommissionCategory, part: 'flat' | 'percent') => {
    const key = keyOf(month, category);
    const value = amounts[key]?.[part] ?? '';
    return (
      <TextField
        size="small"
        label={part === 'flat' ? '$' : '%'}
        value={value}
        onChange={(e) => {
          if (!AMOUNT.test(e.target.value)) return;
          setAmounts((a) => ({
            ...a,
            [key]: {
              flat: a[key]?.flat ?? '',
              percent: a[key]?.percent ?? '',
              [part]: e.target.value,
            },
          }));
        }}
        slotProps={{
          htmlInput: {
            maxLength: 5,
            inputMode: 'decimal',
            'aria-label': `${month ? `Month - ${month}` : 'Current month'} ${category} ${part === 'flat' ? '$' : '%'}`,
          },
        }}
        sx={{ width: 96 }}
      />
    );
  };
  const amountRows = (month: number) =>
    COMMISSION_CATEGORIES.map((category) => (
      <TableRow key={`${month}:${category}`}>
        <TableCell>{category}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {amountField(month, category, 'flat')}
            {amountField(month, category, 'percent')}
          </Box>
        </TableCell>
      </TableRow>
    ));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const found: Problems = {};
    if (mode === 'add' && !name.trim()) found.name = 'Matrix name is required';
    if (!type) found.commissionType = 'Select Matrix commission type';
    if (!state) found.state = 'Select State';
    if (!planId) found.planId = 'Select Plan';
    if (recurring && !monthCount) found.months = 'Select no of month';
    if (Object.keys(found).length) return setProblems(found);
    const rateList: MatrixRate[] = [];
    for (let month = 0; month <= monthCount; month += 1)
      for (const category of COMMISSION_CATEGORIES) {
        const a = amounts[keyOf(month, category)];
        rateList.push({
          month,
          category,
          flatCents: Math.round(Number(a?.flat || 0) * 100),
          percentBp: Math.round(Number(a?.percent || 0) * 100),
        });
      }
    try {
      await save.mutateAsync({
        ...(mode === 'add' ? { name: name.trim() } : {}),
        commissionType: type,
        months: monthCount,
        recurringPlan,
        recurringDevice,
        state,
        planId,
        rates: rateList,
      });
      notify.success(
        mode === 'add'
          ? 'Matrix commission added.'
          : mode === 'update'
            ? 'Matrix commission updated.'
            : 'State added to the matrix.',
      );
      onClose();
    } catch (err) {
      const fields = fromServer(err);
      if (Object.keys(fields).length) setProblems(fields);
      else notify.error(err);
    }
  };

  const stateChoices = mode === 'update' ? (detail?.states ?? []) : US_STATES.map(([code]) => code);
  return (
    <FormDialog
      open
      title={TITLES[mode]}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={save.isPending}
      maxWidth="md"
    >
      <Typography color="text.secondary" sx={{ fontSize: 13, mb: 2 }}>
        Create and manage recurring matrix commission for categories
      </Typography>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <TextField
          required
          label="Matrix name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={mode !== 'add'}
          error={Boolean(problems.name)}
          helperText={problems.name}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <TextField
          select
          required
          label="Matrix commission type"
          value={type}
          onChange={(e) => setType(e.target.value as MatrixCommissionType)}
          error={Boolean(problems.commissionType)}
          helperText={problems.commissionType}
        >
          {MATRIX_COMMISSION_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          required
          label="State"
          value={state}
          onChange={(e) => {
            setState(e.target.value);
            setPlanId('');
          }}
          disabled={Boolean(preset)}
          error={Boolean(problems.state)}
          helperText={problems.state}
        >
          {stateChoices.map((code) => (
            <MenuItem key={code} value={code}>
              {stateName(code)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          required
          label="Select Plan"
          value={planId}
          onChange={(e) => setPlanId(e.target.value)}
          disabled={Boolean(preset) || !state}
          error={Boolean(problems.planId)}
          helperText={problems.planId || (!state ? 'Select state first' : undefined)}
        >
          {preset ? null : (
            <MenuItem value="all">
              <em>All Plans</em>
            </MenuItem>
          )}
          {(plans.data ?? []).map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Typography variant="subtitle2" component="h3" sx={{ mt: 2, mb: 0.5 }}>
        Commission for current month
      </Typography>
      <Table size="small" aria-label="Commission for current month">
        <TableHead>
          <TableRow>
            <TableCell>Category name</TableCell>
            <TableCell>Commission Amount $/%</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{amountRows(0)}</TableBody>
      </Table>
      <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.5 }}>
        Note: If both $ (fixed amount) and % (percentage) are entered for a category, the system
        compares the calculated amounts and applies commission based on the greater value-not both.
      </Typography>

      {recurring ? (
        <Box aria-label="Recurring commission" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
            Recurring commission
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 12, mb: 1 }}>
            Note: If you are applying 12 month&apos;s, commission, There will be as 11 months
            recurring and 1 month as Enrollment commission.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            }}
          >
            <TextField
              select
              required
              label="Months"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
              error={Boolean(problems.months)}
              helperText={problems.months}
            >
              {Array.from({ length: MATRIX_MAX_MONTHS }, (_v, i) => (
                <MenuItem key={i + 1} value={String(i + 1)}>
                  {i + 1}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Recurring for plan commission"
              value={recurringPlan}
              onChange={(e) => setRecurringPlan(e.target.value as 'Yes' | 'No')}
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
            <TextField
              select
              label="Recurring for device commission"
              value={recurringDevice}
              onChange={(e) => setRecurringDevice(e.target.value as 'Yes' | 'No')}
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
          </Box>
          {monthCount > 0 ? (
            <Table size="small" aria-label="Recurring months" sx={{ mt: 1 }}>
              <TableBody>
                {Array.from({ length: monthCount }, (_v, i) => i + 1).flatMap((month) => [
                  <TableRow key={`h${month}`}>
                    <TableCell colSpan={2} sx={{ fontWeight: 600 }}>
                      Month - {month}
                    </TableCell>
                  </TableRow>,
                  ...amountRows(month),
                ])}
              </TableBody>
            </Table>
          ) : null}
          <Typography color="text.secondary" sx={{ fontSize: 12, mt: 1 }}>
            Once a recurring commission is created, the same amount will be automatically applied to
            any newly created states or plans for the selected months.
          </Typography>
        </Box>
      ) : null}
      <Alert severity="info" sx={{ mt: 2 }}>
        How it works: Commission is applied for each duration based on the configured amount for
        every category. If both $ and % are entered, the greater calculated amount is used. Example:
        If plan price is $100 and you enter $15 fixed with 10% ($10), commission will be $15-the
        greater calculated amount.
      </Alert>
      <Button
        sx={{ mt: 1 }}
        onClick={() => {
          setAmounts(rates.data ? toAmounts(rates.data.rates) : start);
          setProblems({});
        }}
      >
        Reset
      </Button>
    </FormDialog>
  );
}

/* --------------------------------- The list ---------------------------------- */

function CopyDialog({ matrix, onClose }: { matrix: CommissionMatrixDto; onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [name, setName] = useState(`${matrix.name} copy`);
  const [problem, setProblem] = useState('');
  const copy = useMutation({
    mutationFn: () => api.post<CommissionMatrixDto>(`${BASE}/${matrix.id}/copy`, { name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['commission'] }),
  });
  return (
    <FormDialog
      open
      title="Copy matrix"
      maxWidth="xs"
      onClose={onClose}
      submitting={copy.isPending}
      onSubmit={(e) => {
        e.preventDefault();
        copy.mutate(undefined, {
          onSuccess: (m) => {
            notify.success(`Matrix copied as ${m.name}.`);
            onClose();
          },
          onError: (err) => {
            const fields = fromServer(err);
            if (fields.name) setProblem(fields.name);
            else notify.error(err);
          },
        });
      }}
    >
      <TextField
        required
        fullWidth
        label="Matrix name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={Boolean(problem)}
        helperText={problem || `A copy of ${matrix.name} with all its lines.`}
        slotProps={{ htmlInput: { maxLength: 100 } }}
      />
    </FormDialog>
  );
}

function RowActions({ matrix, onCopy }: { matrix: CommissionMatrixDto; onCopy: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const status = useMutation({
    mutationFn: (to: 'Active' | 'Inactive') =>
      api.post<CommissionMatrixDto>(`${BASE}/${matrix.id}/status`, { status: to }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['commission'] }),
  });
  const active = matrix.status === 'Active';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Button size="small" component={Link} to={`${LIST_PATH}/${matrix.id}`}>
        Manage matrix commission
      </Button>
      <IconButton
        size="small"
        aria-label={`More actions for ${matrix.name}`}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onCopy();
          }}
        >
          Copy matrix
        </MenuItem>
        <MenuItem
          disabled={status.isPending}
          onClick={() => {
            setAnchor(null);
            status.mutate(active ? 'Inactive' : 'Active', {
              onSuccess: () =>
                notify.success(`${matrix.name} ${active ? 'deactivated' : 'activated'}.`),
              onError: (err) => notify.error(err),
            });
          }}
        >
          {active ? 'Deactivate' : 'Activate'}
        </MenuItem>
      </Menu>
    </Box>
  );
}

/** "Manage Commission Matrix" (PC792). */
export function CommissionMatrixPage() {
  const { can, user } = useAuth();
  const featureOn = useFeatureReleased();
  const [filter, setFilter] = useState<MatrixFilter>('All');
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [copying, setCopying] = useState<CommissionMatrixDto | null>(null);
  const list = useQuery({
    queryKey: ['commission', 'list', filter, q],
    queryFn: () => api.get<CommissionMatrixListDto>(BASE, { filter, q }),
    placeholderData: (prev) => prev,
  });
  const cards = list.data?.cards;
  const columns: Column<CommissionMatrixDto>[] = [
    { key: '#', header: '#', width: 50, render: (_m, i) => rowNumber(i) },
    { key: 'name', header: 'Matrix name', render: (m) => m.name },
    { key: 'type', header: 'Commission type', render: (m) => m.commissionType },
    { key: 'months', header: 'Months', render: (m) => dash(m.months) },
    { key: 'plan', header: 'Recurring for plan commission', render: (m) => dash(m.recurringPlan) },
    {
      key: 'device',
      header: 'Recurring for device commission',
      render: (m) => dash(m.recurringDevice),
    },
    { key: 'employee', header: 'Total employee', align: 'right', render: (m) => m.totals.employee },
    { key: 'retailer', header: 'Total retailer', align: 'right', render: (m) => m.totals.retailer },
    {
      key: 'distributor',
      header: 'Total distributor',
      align: 'right',
      render: (m) => m.totals.distributor,
    },
    { key: 'master', header: 'Total master', align: 'right', render: (m) => m.totals.master },
    { key: 'by', header: 'Created by', render: (m) => m.createdBy?.loginId ?? '' },
    { key: 'at', header: 'Created date', nowrap: true, render: (m) => formatDate(m.createdAt) },
    { key: 'status', header: 'Status', render: (m) => <StatusChip status={m.status} /> },
    {
      key: 'actions',
      header: 'Actions',
      render: (m) => <RowActions matrix={m} onCopy={() => setCopying(m)} />,
    },
  ];
  const card = (label: string, value: string | number) => (
    <Paper variant="outlined" sx={{ p: 1.5 }} key={label}>
      <Typography color="text.secondary" sx={{ fontSize: 12 }}>
        {label}
      </Typography>
      <Typography variant="h3" component="p">
        {value}
      </Typography>
    </Paper>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h2" component="h2">
            Manage Commission Matrix
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 13 }}>
            Create, manage, and copy commission matrices for categories, states, and plans. Supports
            one-time and recurring commission structures.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {featureOn('commission-redeem') ? (
            <CommissionRuleButtons admin={user?.level === 'admin'} />
          ) : null}
          {can('PC1243') ? (
            <Button variant="contained" onClick={() => setAdding(true)}>
              Add matrix commission (PC1243)
            </Button>
          ) : null}
        </Box>
      </Box>
      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <>
          <Box
            aria-label="Matrix summary"
            sx={{
              display: 'grid',
              gap: 1.5,
              mb: 1.5,
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
            }}
          >
            {card('Total Matrices', cards?.total ?? '')}
            {card('Active Matrices', cards?.active ?? '')}
            {card('Inactive Matrices', cards?.inactive ?? '')}
            {card('Avg. Total Employees', cards?.avgEmployees ?? '')}
            {card('Last Updated', cards?.lastUpdated ? formatDateTime(cards.lastUpdated) : '-')}
          </Box>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle1" component="h3" sx={{ mb: 1 }}>
              Matrix List
            </Typography>
            <Box
              sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}
            >
              <TextField
                size="small"
                placeholder="Search matrix name..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                slotProps={{ htmlInput: { 'aria-label': 'Search matrix name', maxLength: 100 } }}
              />
              <ToggleButtonGroup
                size="small"
                exclusive
                value={filter}
                onChange={(_e, v: MatrixFilter | null) => v && setFilter(v)}
                aria-label="Quick filters"
              >
                {MATRIX_FILTERS.map((f) => (
                  <ToggleButton key={f} value={f}>
                    {f}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <DataTable
              label="Matrix List"
              columns={columns}
              rows={list.data?.data}
              rowKey={(m) => m.id}
              loading={list.isFetching}
              emptyText="No record found"
            />
          </Paper>
        </>
      )}
      {adding ? <MatrixDialog mode="add" onClose={() => setAdding(false)} /> : null}
      {copying ? <CopyDialog matrix={copying} onClose={() => setCopying(null)} /> : null}
    </Box>
  );
}

/* ------------------------------ Update commission matrix ---------------------- */

/** "Update commission matrix": the lines of the matrix, one per category, plan and state. */
export function CommissionMatrixDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const [stateDraft, setStateDraft] = useState('');
  const [state, setState] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dialog, setDialog] = useState<{
    mode: 'update' | 'addState';
    preset?: { state: string; planId: string };
  } | null>(null);
  const detail = useQuery({
    queryKey: ['commission', 'detail', id, state, page, pageSize],
    queryFn: () =>
      api.get<CommissionMatrixDetailDto>(`${BASE}/${id}`, {
        ...(state ? { state } : {}),
        page,
        pageSize,
      }),
    placeholderData: (prev) => prev,
  });
  if (detail.error)
    return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  if (!detail.data) return <LoadingState />;
  const { matrix, states, lines } = detail.data;
  const columns: Column<CommissionLineDto>[] = [
    { key: '#', header: '#', width: 50, render: (_l, i) => rowNumber(i, page, pageSize) },
    { key: 'category', header: 'Category', render: (l) => l.category },
    { key: 'plan', header: 'Plan', render: (l) => l.plan },
    { key: 'state', header: 'State', render: (l) => l.state },
    {
      key: 'amount',
      header: 'Amount ($/%)',
      nowrap: true,
      render: (l) => `${formatCents(l.flatCents)} / ${percentLabel(l.percentBp)}`,
    },
    {
      key: 'action',
      header: 'Action',
      render: (l) =>
        can('PC1243') ? (
          <Button
            size="small"
            onClick={() =>
              setDialog({ mode: 'update', preset: { state: l.state, planId: l.planId } })
            }
            aria-label={`Edit ${l.category} ${l.plan} ${l.state}`}
          >
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h2" component="h2">
            Update commission matrix
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 13 }}>
            Manage and update recurring commission matrix for categories
          </Typography>
        </Box>
        <Button component={Link} to={LIST_PATH}>
          Back to list
        </Button>
      </Box>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Box
          component="form"
          aria-label="Matrix filter"
          onSubmit={(e) => {
            e.preventDefault();
            setState(stateDraft);
            setPage(1);
          }}
          sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <TextField size="small" label="Matrix commission name" value={matrix.name} disabled />
          <TextField
            size="small"
            label="Matrix commission type"
            value={matrix.commissionType}
            disabled
          />
          <TextField
            select
            size="small"
            label="Select State"
            value={stateDraft}
            onChange={(e) => setStateDraft(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            {states.map((code) => (
              <MenuItem key={code} value={code}>
                {stateName(code)}
              </MenuItem>
            ))}
          </TextField>
          <Button type="submit" variant="outlined">
            Filter
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          {can('PC1243') ? (
            <>
              <Button
                variant="contained"
                disabled={states.length === 0}
                onClick={() => setDialog({ mode: 'update' })}
              >
                Update matrix commission (PC1243)
              </Button>
              <Button variant="outlined" onClick={() => setDialog({ mode: 'addState' })}>
                Add state to matrix (PC1243)
              </Button>
            </>
          ) : null}
        </Box>
      </Paper>
      <DataTable
        label="Matrix lines"
        columns={columns}
        rows={lines.data}
        rowKey={(l) => l.id}
        loading={detail.isFetching}
        emptyText="No record found"
        page={page}
        pageSize={pageSize}
        total={lines.total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        pageSizeOptions={[10, 25, 50, 100]}
      />
      {dialog ? (
        <MatrixDialog
          key={`${dialog.mode}:${dialog.preset?.state ?? ''}:${dialog.preset?.planId ?? ''}`}
          mode={dialog.mode}
          matrix={matrix}
          detail={detail.data}
          preset={dialog.preset}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </Box>
  );
}
